import { GTM } from '@/constants/GtmConstants'
import {
  SEARCH_PATH,
  SEARCH_ROUTE_NAME,
  SEARCH_V2_PATH
} from '@/constants/SearchConstants'
import {
  accountGuardV2,
  accountParamChangeGuard,
  customerGuardV2
} from '@/router/guards'
import {
  isChunkOrAssetFailure,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'
import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router'

// Lazy route components: keeps view code out of the entry bundle and out of a
// future server bundle (VIP-12036)
const TheAccountPage = () => import('@/views/TheAccountPage.vue')
const TheAllManufacturersPage = () =>
  import('@/views/TheAllManufacturersPage.vue')
const TheAuthenticationPage = () => import('@/views/TheAuthenticationPage.vue')
const TheCartPage = () => import('@/views/TheCartPage.vue')
const TheCategoryPage = () => import('@/views/TheCategoryPage.vue')
const TheCheckoutPage = () => import('@/views/TheCheckoutPage.vue')
const TheCmsPage = () => import('@/views/TheCmsPage.vue')
const TheCollectionPage = () => import('@/views/TheCollectionPage.vue')
const TheErrorPage = () => import('@/views/TheErrorPage.vue')
const TheManufacturerPage = () => import('@/views/TheManufacturerPage.vue')
const TheOpcPage = () => import('@/views/TheOpcPage.vue')
const TheProductDetailPage = () => import('@/views/TheProductDetailPage.vue')
const TheSearchPage = () => import('@/views/TheSearchPage.vue')
const TheSuccessPage = () => import('@/views/TheSuccessPage.vue')
const TheWishListPage = () => import('@/views/TheWishListPage.vue')

export const FALLBACK_ROUTE_NAME = 'fallback'

const routes = [
  // static routes
  { path: '/', name: 'home', component: TheCmsPage },
  {
    path: '/error',
    name: 'error',
    component: TheErrorPage
  },
  {
    path: '/checkout/cart',
    name: 'cart',
    component: TheCartPage
  },
  {
    path: SEARCH_PATH,
    name: SEARCH_ROUTE_NAME,
    component: TheSearchPage
  },
  {
    // The gatekeeper-era URL of the new search (VIP-12207) — keep the query so
    // a shared /search/v2?q=… link still lands on its results.
    path: SEARCH_V2_PATH,
    redirect: ({ query }) => ({ path: SEARCH_PATH, query })
  },
  {
    path: '/merkliste',
    name: 'wishlist',
    component: TheWishListPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/success/:h',
    name: 'checkout_success',
    component: TheSuccessPage
  },
  // dynamic routes
  {
    path: '/auth/v2/:action',
    name: 'authAction',
    component: TheAuthenticationPage
  },
  {
    path: '/:prefix*-pdp-p:pId(\\d+)-c:cId(\\d+):suffix?',
    name: 'pdp',
    component: TheProductDetailPage
  },
  {
    path: '/:prefix*-opc-p:pId(\\d+)-c:cId(\\d+):suffix?',
    name: 'opc',
    component: TheOpcPage
  },
  {
    path: '/:prefix*-man-m:id(\\d+):suffix?',
    name: 'manufacturer',
    component: TheManufacturerPage
  },
  {
    path: '/winery-a-z',
    name: 'winery_a_z',
    component: TheAllManufacturersPage
  },
  {
    path: '/:prefix*-col-c:id(\\d+):suffix?',
    name: 'collection',
    component: TheCollectionPage
  },
  {
    path: '/:prefix*-cat-c:id(\\d+):suffix?',
    name: 'category',
    component: TheCategoryPage
  },
  {
    path: '/:prefix*-cms-p:id(\\d+):suffix?',
    name: 'cms',
    component: TheCmsPage
  },
  {
    path: '/checkout/v2',
    name: 'checkout_default',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Address routes
  {
    path: '/checkout/v2/address',
    props: true,
    name: 'checkout_address',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/address/:type',
    props: true,
    name: 'checkout_address_type',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/address/:type/:action',
    props: true,
    name: 'checkout_address_action',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Payment routes
  {
    path: '/checkout/v2/payment',
    props: true,
    name: 'checkout_payment',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/payment/:action',
    props: true,
    name: 'checkout_payment_action',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Delivery route
  {
    path: '/checkout/v2/delivery',
    props: true,
    name: 'checkout_delivery',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Review route
  {
    path: '/checkout/v2/review',
    props: true,
    name: 'checkout_review',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Guest checkout routes
  {
    path: '/checkout/guest',
    name: 'checkout_guest_default',
    component: TheCheckoutPage
  },
  // Guest address routes
  {
    path: '/checkout/guest/address',
    props: true,
    name: 'checkout_guest_address',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/address/:type',
    props: true,
    name: 'checkout_guest_address_type',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/address/:type/:action',
    props: true,
    name: 'checkout_guest_address_action',
    component: TheCheckoutPage
  },
  // Guest payment routes
  {
    path: '/checkout/guest/payment',
    props: true,
    name: 'checkout_guest_payment',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/payment/:action',
    props: true,
    name: 'checkout_guest_payment_action',
    component: TheCheckoutPage
  },
  // Guest delivery route
  {
    path: '/checkout/guest/delivery',
    props: true,
    name: 'checkout_guest_delivery',
    component: TheCheckoutPage
  },
  // Guest review route
  {
    path: '/checkout/guest/review',
    props: true,
    name: 'checkout_guest_review',
    component: TheCheckoutPage
  },
  // Account management routes. accountGuardV2 applies the legacy per-tab auth
  // levels and keeps not-yet-migrated tabs admin-only (VIP-12097).
  {
    path: '/account/manage/v2',
    name: 'account_default',
    component: TheAccountPage,
    beforeEnter: accountGuardV2
  },
  {
    // The optional second segment carries a record id for tabs that have a detail view -
    // today the orders tab's order increment id (VIP-12078). Tabs without one get '',
    // which is why no `props: true` here: TheAccountPage declares no props, so the
    // params would fall through as attributes and `id` would blank out its own
    // id="content" (breaking scroll-to-top and the e2e selector). TheAccountTemplate
    // reads route.params itself.
    path: '/account/manage/v2/:action/:id?',
    name: 'account_action',
    component: TheAccountPage,
    beforeEnter: accountGuardV2
  },
  { path: '/:catchAll(.*)', name: FALLBACK_ROUTE_NAME, component: TheErrorPage }
]

/**
 * Now that route components are lazy, a rotated chunk hash after a deploy makes
 * their loader reject, and vue-router hands that to its error listeners. With no
 * listener the rejection is swallowed — `RouterLink.navigate` catches it with a
 * noop and the initial navigation only warns in dev — so a click would become a
 * silent no-op. Recover the same way as the other chunk-failure paths
 * (VIP-12034); anything else keeps the console.error vue-router would have done
 * on its own.
 *
 * @param {unknown} error
 */
export const handleRouterError = (error) => {
  if (isChunkOrAssetFailure(error)) {
    tryReloadForChunkError()
    return
  }
  console.error(error)
}

/**
 * Router factory (VIP-12037): the server entry creates a fresh router per
 * request — a shared instance would leak navigation state between users. The
 * client keeps using the module singleton below so every existing
 * `import router from '@/router'` still refers to the app's router.
 *
 * The navigation guards are client concerns (session, tracking, scrolling)
 * and cache store instances at module level, so they are only attached in the
 * browser build; attaching them on the server would also leave navigations
 * hanging (the guard bails without calling next()).
 */
export const createAppRouter = () => {
  const router = createRouter({
    history: import.meta.env.SSR
      ? createMemoryHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL),
    routes
  })

  if (!import.meta.env.SSR) {
    _attachClientGuards(router)
  }

  return router
}

const router = createAppRouter()

function _attachClientGuards(router) {
  router.onError(handleRouterError)

  // Must stay ahead of the tracking/session hook below: it is a gate, not a
  // side effect. See accountParamChangeGuard for why the route's own
  // beforeEnter is not enough.
  router.beforeEach(accountParamChangeGuard)

  router.beforeEach(
    async (
      { path: to, name: toName, query },
      { path: from, name: fromName },
      next
    ) => {
      if (fromName && from === to) {
        next()
        return
      }

      if (typeof window === 'undefined') {
        console.warn('window is undefined')
        return
      }

      // Check for original-customer-id parameter
      if (query['original-customer-id']) {
        const { customer } = await _getModules()
        await customer.waitUntilPending()

        // Wait for profile data to be loaded (with timeout)
        let attempts = 0
        const maxAttempts = 20 // 2 seconds max wait
        while (
          attempts < maxAttempts &&
          !customer.profile &&
          !customer.profileV2
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          attempts++
        }

        // If profile data still isn't loaded after waiting, show error banner to be safe
        if (
          attempts >= maxAttempts &&
          !customer.profile &&
          !customer.profileV2
        ) {
          customer.setAdminPermissionError(true)
          const newQuery = { ...query }
          delete newQuery['original-customer-id']
          next({ path: to, query: newQuery })
          return
        }

        if (customer.customerIsAdmin) {
          // Admin is logged in, set the cookie and clear any existing error
          const { default: UtilityHelper } =
            await import('@/services/helpers/UtilityHelper')
          await UtilityHelper.setOriginalCustomerId(
            query['original-customer-id'],
            {
              refreshCart: true,
              reloadPage: true
            }
          )
          customer.setAdminPermissionError(false)

          // Clear the query parameter to avoid showing it in the URL
          const newQuery = { ...query }
          delete newQuery['original-customer-id']
          next({ path: to, query: newQuery })
          return
        } else {
          // Not admin, show error banner
          customer.setAdminPermissionError(true)

          // Clear the query parameter to avoid showing it in the URL
          const newQuery = { ...query }
          delete newQuery['original-customer-id']
          next({ path: to, query: newQuery })
          return
        }
      } else {
        // Check if original-customer-id cookie is set on every route change
        const { default: CookieHelper } =
          await import('@/services/helpers/CookieHelper')
        const isAdminLoggedInAsCustomer = !!CookieHelper.getCookie(
          'original-customer-id'
        )

        // Update admin permission error state based on cookie presence
        const { customer } = await _getModules()
        await customer.waitUntilPending()

        if (isAdminLoggedInAsCustomer || customer.customerIsAdmin) {
          // Clear admin permission error if admin is logged in as customer or if user is admin
          customer.setAdminPermissionError(false)
        }
      }

      if (!toName.includes('checkout') && !toName.includes('opc')) {
        const { checkout } = await _getModules()
        checkout.clearData()
      }

      // Clear guest checkout data when navigating away from guest routes
      // (either to non-checkout routes or to regular checkout routes)
      if (
        fromName &&
        fromName.includes('checkout_guest') &&
        !toName.includes('checkout_guest')
      ) {
        const { guestCheckout } = await _getModules()
        if (guestCheckout) {
          guestCheckout.clearData()
        }
      }

      void _refreshData()
      void _setupDataLayer(toName)

      next()
    }
  )

  router.afterEach(async (to, from) => {
    const { WALKER_COMMANDS, elb, DomHelper, customer, features } =
      await _getModules()

    if (typeof window === 'undefined') {
      console.warn('window is undefined')
      return
    }

    if (from.path !== to.path && !to.path.includes('-opc-')) {
      DomHelper.scrollTo('#root')
    }

    await customer.establishSession()

    // Wait for profile data to be ready
    const waitForProfile = async () => {
      const maxAttempts = 10
      let attempts = 0
      while (!customer.customerProfile?.customer_id && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 500))
        attempts++
      }
    }

    await waitForProfile()
    // Without this, the page view emitted by 'walker run' below would race the
    // features request and mostly lose gatekeeper_settings (VIP-12234).
    await features.whenReady()

    const elbData = {
      customerId: customer.customerProfile.customer_id,
      completedOrders: customer.customerProfile.completed_orders_count,
      lastOrderDaysAgo: customer.customerProfile.days_since_last_order
    }

    const { getVwoExperimentsForElbwalker } =
      await import('@/services/helpers/VwoExperimentsHelper')
    const vwoData = getVwoExperimentsForElbwalker()
    Object.assign(elbData, vwoData)
    Object.assign(elbData, features.gatekeeperForElbwalker)

    elb(WALKER_COMMANDS.run, { user: elbData })
  })
}

const _refreshData = async () => {
  const { wishlist, customer, lazyStore } = await _getModules()
  await customer.establishSession()
  await lazyStore.setForceLoad({ value: false })

  void wishlist.refreshWishlistItems()
  void wishlist.refreshWishlistSoldOutItems()
}

const _setupDataLayer = async (module) => {
  const { TagManager } = await _getModules()
  TagManager.pageUpdate(module, GTM[module])
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { useNewCheckoutStore },
      { useCustomerStore },
      { useWishlistStore },
      { useLazyStore },
      { useGuestCheckoutStore },
      { useFeaturesStore },
      { default: TagManager },
      { elb },
      { GLOBAL_STORE },
      { WALKER_COMMANDS },
      { default: DomHelper }
    ] = await Promise.all([
      await import('@/stores/newCheckout.js'),
      await import('@/stores/customer'),
      await import('@/stores/wishlist'),
      await import('@/stores/lazyStore'),
      await import('@/stores/guestCheckout'),
      await import('@/stores/features'),
      await import('@/services/analytics/TagManager'),
      await import('@elbwalker/walker.js'),
      await import('@/constants/GlobalConstants'),
      await import('@/services/helpers/ElbwalkerHelper'),
      await import('@/services/helpers/DomHelper')
    ])
    cache = {
      get checkout() {
        return useNewCheckoutStore()
      },
      get customer() {
        return useCustomerStore()
      },
      get wishlist() {
        return useWishlistStore()
      },
      get lazyStore() {
        return useLazyStore()
      },
      get guestCheckout() {
        return useGuestCheckoutStore()
      },
      get features() {
        return useFeaturesStore()
      },
      TagManager,
      elb,
      GLOBAL_STORE,
      WALKER_COMMANDS,
      DomHelper
    }
    return cache
  }
})()

export default router
